<template>
  <div class="trace-list">
    <div v-if="loading && !traces.length" class="list-note">Loading traces…</div>
    <div v-else-if="error" class="list-note err">Couldn't load traces.</div>

    <div v-else-if="!traces.length" class="list-empty">
      <GoblinIcon variant="empty" :size="48" />
      <p class="list-empty-title">No traces yet</p>
      <p class="list-empty-sub">Spans show up here as your services report them.</p>
    </div>

    <ul v-else class="rows">
      <li v-for="t in traces" :key="t.traceId">
        <button
          type="button"
          class="trace-row"
          :class="{ 'is-active': t.traceId === selectedId }"
          :title="`Open trace ${t.rootName}`"
          @click="$emit('pick', t)"
        >
          <span class="row-top">
            <span class="root-name">{{ t.rootName }}</span>
            <span class="rel-time">{{ relTime(t.ts) }}</span>
          </span>

          <span class="row-mid">
            <span class="service">{{ t.rootService }}</span>
            <span v-if="t.httpMethod && t.httpRoute" class="http">
              {{ t.httpMethod }} {{ t.httpRoute }}
            </span>
            <span v-if="t.userId" class="user">
              <User :size="11" aria-hidden="true" />{{ t.userId }}
            </span>
          </span>

          <span class="row-bottom">
            <span class="dur">{{ formatDurationNs(t.durationNs) }}</span>
            <span class="dur-track" aria-hidden="true">
              <span class="dur-bar" :style="{ width: durPct(t) }" />
            </span>
            <span class="spans">{{ t.spanCount }} {{ t.spanCount === 1 ? 'span' : 'spans' }}</span>
            <span v-if="t.errorCount > 0" class="err-badge">
              <CircleAlert :size="11" aria-hidden="true" />
              {{ t.errorCount }} {{ t.errorCount === 1 ? 'error' : 'errors' }}
            </span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CircleAlert, User } from 'lucide-vue-next';
import type { TraceSummary } from '../../api.js';
import { formatRelativeTime } from '../../lib/format-metric.js';
import { formatDurationNs } from '../../lib/trace-layout.js';
import GoblinIcon from '../GoblinIcon.vue';

const props = defineProps<{
  traces: TraceSummary[];
  /** traceId of the trace currently open in the detail pane. */
  selectedId: string | null;
  loading?: boolean;
  error?: boolean;
}>();

defineEmits<{ pick: [trace: TraceSummary] }>();

/** Longest trace in the current list — every row's mini bar is relative to it
 * so the eye can compare durations at a glance. */
const maxDurationNs = computed(() =>
  props.traces.reduce((max, t) => Math.max(max, t.durationNs), 1),
);

function durPct(t: TraceSummary): string {
  const frac = Math.min(1, Math.max(0.02, t.durationNs / maxDurationNs.value));
  return `${(frac * 100).toFixed(1)}%`;
}

function relTime(ts: number): string {
  const rel = formatRelativeTime(ts);
  return rel === 'now' ? 'just now' : `${rel} ago`;
}
</script>

<style scoped>
.trace-list {
  min-height: 0;
  height: 100%;
  overflow-y: auto;
}
.list-note {
  padding: var(--lg-space-3);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.list-note.err {
  color: var(--lg-danger);
}
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-6) var(--lg-space-3);
  text-align: center;
  color: var(--lg-text-faint);
}
.list-empty-title {
  margin: 0;
  color: var(--lg-text-dim);
  font-size: var(--lg-fs-md);
}
.list-empty-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
}

.rows {
  margin: 0;
  padding: 0;
  list-style: none;
}
.trace-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  padding: var(--lg-space-2) var(--lg-space-3);
  border: none;
  border-left: 3px solid transparent;
  border-bottom: 1px solid var(--lg-border-faint);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.trace-row:hover {
  background: var(--lg-bg-hover);
}
.trace-row.is-active {
  border-left-color: var(--lg-accent);
  background: var(--lg-bg-active);
}
.trace-row:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--lg-accent);
}

.row-top {
  display: flex;
  align-items: baseline;
  gap: var(--lg-space-2);
  min-width: 0;
}
.root-name {
  flex: 1;
  min-width: 0;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rel-time {
  flex-shrink: 0;
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
  white-space: nowrap;
}

.row-mid {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  min-width: 0;
  color: var(--lg-text-dim);
  font-size: var(--lg-fs-xs);
}
.service {
  flex-shrink: 0;
}
.http {
  font-family: var(--lg-font-mono);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.user {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  color: var(--lg-text-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 12ch;
}

.row-bottom {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  min-width: 0;
}
.dur {
  flex-shrink: 0;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xs);
  color: var(--lg-text);
  font-variant-numeric: tabular-nums;
  min-width: 7ch;
}
.dur-track {
  flex: 1;
  min-width: 24px;
  height: 4px;
  border-radius: var(--lg-radius-pill);
  background: var(--lg-bg-surface);
  overflow: hidden;
}
.dur-bar {
  display: block;
  height: 100%;
  border-radius: var(--lg-radius-pill);
  background: var(--lg-accent);
  opacity: 0.75;
}
.spans {
  flex-shrink: 0;
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
  white-space: nowrap;
}
.err-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 0 var(--lg-space-1);
  border-radius: var(--lg-radius-sm);
  color: var(--lg-danger);
  background: color-mix(in srgb, var(--lg-danger) 14%, transparent);
  font-size: var(--lg-fs-2xs);
  font-weight: 700;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .trace-row {
    min-height: var(--lg-touch);
    justify-content: center;
  }
}
</style>
